<template>
    <v-dialog
      v-model="isOpen"
      max-width="600px"
      persistent
      scroll-strategy="block"
      retain-focus
      class="cruise-form-dialog"
      @click:outside="refocusPurchaseDialog"
    >
      <div class="bg-white rounded-xl p-6 md:p-8 shadow-lg dialog-content">
        <!-- Close button for better mobile accessibility -->
        <button @click="closeDialog" class="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700">
          <v-icon>mdi-close</v-icon>
        </button>
        
        <h2 class="text-xl md:text-2xl font-bold mb-2 text-slate-800  pr-8">
          Create Your Cruise <span class="text-yellow-500">{{ packageName }}</span>
        </h2>
        <p class="mb-6 text-gray-600 ">
          Set up your {{ packageName }} package cruise details to continue
        </p>
        
        <v-form @submit.prevent="proceedToCheckout" v-model="isFormValid">
          <v-text-field
            ref="cruiseNameRef"
            v-model="cruiseName"
            label="Cruise Name"
            :rules="[v => !!v || 'Cruise name is required']"
            variant="outlined"
            color="primary"
            class="mb-4 text-gray-600 "
          ></v-text-field>
          
          <v-text-field
            v-model.number="quantity"
            label="Number of Ducks"
            type="number"
            min="1"
            :rules="[ 
              v => !!v || 'Quantity is required',
              v => v >= 1 || 'Minimum quantity is 1'
            ]"
            variant="outlined"
            color="primary"
            class="mb-4 text-gray-600 "
          ></v-text-field>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              type="date"
              :rules="[ 
                v => !!v || 'Start date is required',
                v => (!isTemplateSelectionRequired || (v && new Date(v) >= new Date(minStartDate))) || ('Start date must be on or after ' + minStartDate),
                v => !endDate || new Date(v) <= new Date(endDate) || 'Start date must be before end date'
              ]"
              variant="outlined"
              color="primary"
              class="text-gray-600"
              :min="minStartDate"
            ></v-text-field>
            
            <v-text-field
              v-model="endDate"
              label="End Date"
              type="date"
              :rules="[ 
                v => !!v || 'End date is required',
                v => !startDate || new Date(v) >= new Date(startDate) || 'End date must be after start date'
              ]"
              variant="outlined"
              color="primary"
              class="text-gray-600"
              :min="startDate || minStartDate"
            ></v-text-field>
          </div>

          <!-- Shipping lead time hint for non-augmentable packages -->
          <div v-if="isTemplateSelectionRequired" class="bg-amber-50  border-l-4 border-amber-500 p-4 rounded mb-4 text-left flex items-start gap-3">
            <v-icon color="warning">mdi-truck-delivery</v-icon>
            <div>
              <p class="font-semibold text-amber-800 ">Shipping Lead Time</p>
              <p class="text-amber-700 text-sm">
                For this package, your cruise must start on or after <span class="font-semibold">{{ minStartDate }}</span> to allow time for production and shipping.
              </p>
            </div>
          </div>

          <!-- Template Selection (required for packages that don't allow additional ducks) -->
          <div v-if="isTemplateSelectionRequired" class="mb-4">
            <h3 class="text-lg font-semibold mb-3 text-slate-800">Choose your preferred sticker</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Standard QR Layout Template -->
              <div 
                class="template-option"
                :class="{ 'selected': selectedTemplate === 'StandardQRLayout' }"
                @click="selectedTemplate = 'StandardQRLayout'"
              >
                <img 
                  src="/TemplateImages/StandardQRLayout.webp" 
                  alt="Standard QR Layout Template"
                  class="template-image"
                  loading="lazy"
                />
              </div>

              <!-- Modern Template -->
              <div 
                class="template-option"
                :class="{ 'selected': selectedTemplate === 'ModernTemplate' }"
                @click="selectedTemplate = 'ModernTemplate'"
              >
                <img 
                  src="/TemplateImages/ModernTemplate.webp" 
                  alt="Modern Template"
                  class="template-image"
                  loading="lazy"
                />
              </div>
            </div>
            
            <p v-if="!selectedTemplate" class="text-sm text-red-600 mt-2">
              Please select a template to continue
            </p>
          </div>
          
          <!-- Warning about dates not being modifiable -->
          <div class="bg-amber-50  border-l-4 border-amber-500 p-4 rounded mb-6 text-left flex items-start gap-3">
            <v-icon color="warning">mdi-alert</v-icon>
            <div>
              <p class="font-semibold text-amber-800 ">Important:</p>
              <p class="text-amber-700 text-sm">
                The start and end dates cannot be modified after creating your cruise. Please choose these dates carefully.
              </p>
            </div>
          </div>
          
          <div class="bg-blue-50  p-4 rounded-lg mb-6">
            <div class="flex justify-between mb-2 text-gray-600 ">
              <span>Price per duck:</span>
              <span>{{ packagePrice }}</span>
            </div>
            <div class="flex justify-between font-bold text-gray-600 ">
              <span>Total price:</span>
              <span>{{ calculateTotalPrice() }}</span>
            </div>
          </div>
          
          <div class="flex flex-wrap gap-4 justify-end">
            <v-btn 
              color="error" 
              variant="text" 
              @click="closeDialog"
              :disabled="processing"
            >
              Cancel
            </v-btn>
            <v-btn 
              color="primary" 
              type="submit"
              :disabled="!isFormValid || !cruiseName || processing || (isTemplateSelectionRequired && !selectedTemplate)"
              :loading="processing"
            >
              Continue to Checkout
            </v-btn>
          </div>
        </v-form>
        
        <!-- Notifications -->
        <v-snackbar
          v-model="showNotification"
          :color="notificationColor"
          :timeout="3000"
          location="top"
        >
          {{ notificationMessage }}
        </v-snackbar>
      </div>
    </v-dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch, nextTick } from 'vue';
  import axios from 'axios';
  import { Constant } from '@/config/constants';
  import { useAuthStore } from '@/stores/authStore';

  // Props
  const props = defineProps({
    modelValue: {
      type: Boolean,
      default: false
    },
    packageId: {
      type: String,
      required: true
    },
    packageName: {
      type: String,
      required: true
    },
    packagePrice: {
      type: String,
      required: true
    },
    packageCurrency: {
      type: String,
      default: 'USD'
    },
    packageData: {
      type: Object as () => { allowsAdditionalDucks?: boolean },
      default: () => ({})
    }
  });

  // Emits
  const emit = defineEmits(['update:modelValue', 'checkout-success', 'session-expired']);

  // Dialog visibility
  const isOpen = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
  });

  // Refs for focusing inputs
  const cruiseNameRef = ref<HTMLInputElement | null>(null);

  // Auth store
  const authStore = useAuthStore();

  // Form state
  const cruiseName = ref('');
  const quantity = ref(1);
  const startDate = ref('');
  const endDate = ref('');
  const isFormValid = ref(false);
  const processing = ref(false);

  // Template selection state
  const selectedTemplate = ref<string | null>(null);

  // Notification state
  const showNotification = ref(false);
  const notificationMessage = ref('');
  const notificationColor = ref('success');

  // Calculate minimum allowed date handled by minStartDate below

  // Minimum start date: if additional ducks are not allowed (template required), enforce today + 7 days for shipping
  const minStartDate = computed(() => {
    const base = new Date();
    if (isTemplateSelectionRequired.value) {
      base.setDate(base.getDate() + 7);
    }
    return base.toISOString().split('T')[0];
  });

  // Check if template selection is required (for packages that don't allow additional ducks)
  const isTemplateSelectionRequired = computed(() => {
    return props.packageData?.allowsAdditionalDucks === false;
  });

  // Helper function to calculate total price
  const calculateTotalPrice = () => {
    const priceValue = parseFloat(props.packagePrice.replace(/[^0-9.-]+/g, ''));
    if (isNaN(priceValue)) return props.packagePrice;
    
    const totalPrice = priceValue * quantity.value;
    return props.packagePrice.replace(/[0-9.,]+/, totalPrice.toFixed(2));
  };

  // Close the dialog
  const closeDialog = () => {
    isOpen.value = false;
    resetForm();
  };

  // When dialog opens, focus first field for better mobile UX
  watch(isOpen, (open) => {
    if (open) {
      nextTick(() => {
        cruiseNameRef.value?.focus?.();
      });
    }
  });

  // Keep focus within the dialog when user taps outside on mobile
  const refocusPurchaseDialog = () => {
    nextTick(() => {
      cruiseNameRef.value?.focus?.();
    });
  };

  // Reset form values
  const resetForm = () => {
    cruiseName.value = '';
    quantity.value = 1;
    startDate.value = '';
    endDate.value = '';
    selectedTemplate.value = null;
  };

  // Proceed to checkout
  const proceedToCheckout = async () => {
    if (!props.packageId || !cruiseName.value || quantity.value < 1 || !startDate.value || !endDate.value) return;
    
    // Check token validity before proceeding
    if (!authStore.isTokenValid()) {
      notificationMessage.value = 'Your session has expired. Please log in again.';
      notificationColor.value = 'error';
      showNotification.value = true;
      
      setTimeout(() => {
        emit('session-expired');
        closeDialog();
      }, 2000);
      return;
    }
    
    // Enforce shipping lead time start >= minStartDate when template selection is required
    if (isTemplateSelectionRequired.value && new Date(startDate.value) < new Date(minStartDate.value)) {
      notificationMessage.value = `Start date must be on or after ${minStartDate.value} for shipping.`;
      notificationColor.value = 'warning';
      showNotification.value = true;
      return;
    }

    try {
      processing.value = true;
      
      const requestData: any = {
        packageId: parseInt(props.packageId),
        cruiseName: cruiseName.value.trim(),
        quantity: quantity.value,
        startDate: startDate.value,
        endDate: endDate.value
      };

      // Add template if required and selected
      if (isTemplateSelectionRequired.value && selectedTemplate.value) {
        requestData.template = selectedTemplate.value;
      }
      
      const response = await axios.post(`${Constant.API_URL}/auth/checkout`, requestData);
      
      if (response.data.checkoutUrl) {
        emit('checkout-success', response.data.checkoutUrl);
        closeDialog();
      } else {
        throw new Error('No checkout URL provided');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      
      if (error.response?.status === 401) {
        notificationMessage.value = 'Your session has expired. Please log in again.';
        notificationColor.value = 'error';
        showNotification.value = true;
        
        setTimeout(() => {
          emit('session-expired');
          closeDialog();
        }, 2000);
      } else {
        notificationMessage.value = error.response?.data?.message || 'Failed to create checkout session';
        notificationColor.value = 'error';
        showNotification.value = true;
      }
    } finally {
      processing.value = false;
    }
  };
</script>

<style scoped>
/* Dialog styles */
:deep(.v-overlay__content) {
  width: 100%;
  max-width: 600px;
  margin: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 100%;
}

/* Make dialog content scrollable */
.dialog-content {
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  width: 100%;
}

/* Mobile-specific styles */
@media (max-width: 600px) {
  :deep(.v-overlay__content) {
    margin: 0;
    width: 100%;
    height: 100%;
    align-items: flex-start;
  }
  
  .dialog-content {
    max-height: 100vh;
    border-radius: 0;
    padding-bottom: 80px; /* Extra padding at bottom for better scrolling experience */
  }
  
  /* Ensure buttons are large enough for touch */
  :deep(.v-btn) {
    min-height: 44px;
  }
}

/* Improved form field focus styles */
:deep(.v-field--focused) {
  border-color: #eab308 !important;
}

/* Improve scrollbar appearance */
.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
  background: transparent;
}

.dialog-content::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 4px;
}

/* Template selection styles */
.template-option {
  cursor: pointer;
  border: 3px solid transparent;
  border-radius: 12px;
  transition: all 0.3s ease;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 8px;
}

.template-option:hover {
  border-color: #e3f2fd;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.template-option.selected {
  border-color: #2196f3;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  box-shadow: 0 6px 20px rgba(33, 150, 243, 0.25);
}

.template-image {
  width: 100%;
  height: 280px;
  object-fit: contain;
  border-radius: 8px;
  transition: transform 0.3s ease;
  background: white;
  padding: 8px;
}

.template-option:hover .template-image {
  transform: scale(1.02);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .template-image {
    height: 220px;
  }
}

</style>